import React, { useEffect, useState } from "react";
import { SocItem, SocStatus } from "../../../domain/SocItem";
import { DialogType, EntityDialog } from "./types";
import { ActivityFeed } from "@ofqual/portal-activity-feed";
import { buildDefinitions } from "./buildDefinitions";
import { RenderDialog } from "./RenderDialog";
import { useAuth, useLookup } from "@ofqual/portal-core/core/hooks";
import { authSelector } from "../../../redux/selectors";
import { UserType } from "../../../redux/types";
import { OfqualOnlyActivityTypes } from "../../../domain/ActivityType";
import { eventTypesService, noteTypesService } from "../../../services";
import { QueryParams } from "@ofqual/portal-core/core/types";

type SocActivityFeedProps = {
  soc: SocItem;
};

export const SocActivityFeed: React.FC<SocActivityFeedProps> = ({ soc }) => {
  const [focussedEntity, setFocussedEntity] = useState<EntityDialog | null>(
    null
  );
  const { userType } = useAuth(authSelector);

  const [noteTypeLookups, eventTypeLookups] = useLookup(
    noteTypesService,
    eventTypesService
  );

  const definitions = buildDefinitions(
    (activity, dialogType) => {
      setFocussedEntity({
        dialogType: dialogType || DialogType.ActivityAction,
        focussedActivity: activity,
      });
    },
    soc,
    noteTypeLookups,
    eventTypeLookups
  );

  const [
    defaultExcludeActivityTypes,
    setDefaultActivityTypes,
  ] = useState<QueryParams>({ excludeActivityTypes: OfqualOnlyActivityTypes });
  useEffect(() => {
    if (userType !== UserType.Ofqual) {
      setDefaultActivityTypes({
        excludeActivityTypes: OfqualOnlyActivityTypes,
      });
    } else {
      setDefaultActivityTypes({});
    }
  }, [userType, soc]);

  const defaultActions =
    userType === UserType.Ofqual
      ? [
        {
          label: "Internal Message",
          dataTest: "internal-message-button",
          action: () =>
            setFocussedEntity({
              dialogType: DialogType.InternalMessage,
            }),
          disabled: soc.status === SocStatus.Closed,
        },
        {
          label: "Add note",
          dataTest: "case-note-button",
          action: () =>
            setFocussedEntity({
              dialogType: DialogType.CaseNote,
            }),
          disabled: soc.status === SocStatus.Closed,
        },
        {
          label: "Send Message",
          dataTest: "send-message",
          action: () =>
            setFocussedEntity({
              dialogType: DialogType.SendMessage,
            }),
          disabled: soc.status === SocStatus.Closed,
        },
        {
          label: "Add event",
          disabled: soc.status === SocStatus.Closed,
          dataTest: "add-event-button",
          action: () =>
            setFocussedEntity({
              dialogType: DialogType.AddEvent,
            }),
        },
      ]
      : [
        {
          label: "Send Message",
          dataTest: "send-message",
          action: () =>
            setFocussedEntity({
              dialogType: DialogType.SendMessage,
            }),
          disabled: soc.status === SocStatus.Closed,
        },
      ];

  return (
    <>
      <ActivityFeed
        resourceId={soc.id}
        definitions={definitions}
        params={defaultExcludeActivityTypes}
        specialSegmentLabel={(activity) =>
          activity.activityType === 45 ? "Events" : null
        }
        actions={userType === UserType.Magic ? [] : defaultActions}
      />
      <RenderDialog
        focussedEntity={focussedEntity}
        onClose={() => setFocussedEntity(null)}
        soc={soc}
      />
    </>
  );
};
